import React, { useState, useEffect } from "react";
import { CSpinner, CInputGroup, CInputGroupText, CFormInput } from "@coreui/react-pro";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/eye-slash.svg";
import { getProfile, changePassword } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const ChangePassword = () => {
  const [isOldPasswordVisible, setIsOldPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await getProfile();
      setEmail(response.data.email || "");
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    old_password: Yup.string().required("Current password is required"),
    new_password: Yup.string()
      .required("New password is required")
      .min(6, "Password must be at least 6 characters")
      .max(100, "Password must be less than 100 characters"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
      .required("Confirm new password is required"),
  });

  const toggleOldPasswordVisibility = () => {
    setIsOldPasswordVisible(!isOldPasswordVisible);
  };

  const toggleNewPasswordVisibility = () => {
    setIsNewPasswordVisible(!isNewPasswordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setIsConfirmPasswordVisible(!isConfirmPasswordVisible);
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmittingForm(true);
    try {
      const payload = {
        email: email,
        old_password: values.old_password,
        new_password: values.new_password,
        confirm_password: values.confirm_password,
      };
      await changePassword(payload);
      toast.success("Password changed successfully!");
      navigate("/profile");
    } catch (error) {
      console.error("Failed to change password:", error);
      toast.error(error.message || "Failed to change password. Please try again.");
      setErrors({ submit: "Failed to change password" });
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <Skeleton height={30} width="100%" />
            <Skeleton height={30} width="100%" style={{ marginTop: 10 }} />
            <Skeleton height={30} width="100%" style={{ marginTop: 10 }} />
            <div className="d-flex justify-content-end" style={{ marginTop: 20 }}>
              <Skeleton height={50} width={150} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={{
            old_password: "",
            new_password: "",
            confirm_password: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="old_password" className="form-label">
                        <span className="text-danger">*</span> Current Password
                      </label>
                      <CInputGroup className="">
                        <Field
                          name="old_password"
                          as={CFormInput}
                          type={isOldPasswordVisible ? "text" : "password"}
                          placeholder="Enter current password"
                          className={`form-control ${
                            touched.old_password && errors.old_password ? "is-invalid" : ""
                          }`}
                        />
                        <CInputGroupText onClick={toggleOldPasswordVisibility} style={{ cursor: 'pointer' }}>
                          {isOldPasswordVisible ? <EyeIcon style={{ width: '20px', height: '20px' }} /> : <EyeSlashIcon style={{ width: '20px', height: '20px' }} />}
                        </CInputGroupText>
                        <ErrorMessage name="old_password" component="div" className="invalid-feedback" />
                      </CInputGroup>
                    </div>
                    </div>
                    <div className="row">

                    <div className="col-md-6 mb-3">
                      <label htmlFor="new_password" className="form-label">
                        <span className="text-danger">*</span> New Password
                      </label>
                      <CInputGroup className="">
                        <Field
                          name="new_password"
                          as={CFormInput}
                          type={isNewPasswordVisible ? "text" : "password"}
                          placeholder="Enter new password"
                          className={`form-control ${
                            touched.new_password && errors.new_password ? "is-invalid" : ""
                          }`}
                        />
                        <CInputGroupText onClick={toggleNewPasswordVisibility} style={{ cursor: 'pointer' }}>
                          {isNewPasswordVisible ? <EyeIcon style={{ width: '20px', height: '20px' }} /> : <EyeSlashIcon style={{ width: '20px', height: '20px' }} />}
                        </CInputGroupText>
                        <ErrorMessage name="new_password" component="div" className="invalid-feedback" />
                      </CInputGroup>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="confirm_password" className="form-label">
                        <span className="text-danger">*</span> Confirm New Password
                      </label>
                      <CInputGroup className="">
                        <Field
                          name="confirm_password"
                          as={CFormInput}
                          type={isConfirmPasswordVisible ? "text" : "password"}
                          placeholder="Confirm new password"
                          className={`form-control ${
                            touched.confirm_password && errors.confirm_password ? "is-invalid" : ""
                          }`}
                        />
                        <CInputGroupText onClick={toggleConfirmPasswordVisibility} style={{ cursor: 'pointer' }}>
                          {isConfirmPasswordVisible ? <EyeIcon style={{ width: '20px', height: '20px' }} /> : <EyeSlashIcon style={{ width: '20px', height: '20px' }} />}
                        </CInputGroupText>
                        <ErrorMessage name="confirm_password" component="div" className="invalid-feedback" />
                      </CInputGroup>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Change Password
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default ChangePassword;
